// Helper function to build hierarchical entity names for breadcrumbs
export const buildHierarchicalEntityNames = (
  params: Record<string, string>,
  entityData: {
    areaName?: string;
    modelName?: string;
    routeName?: string;
  } = {}
): Record<string, string> => {
  const entityNames: Record<string, string> = {};

  // Map slugs/Ids to names
  if (
    (params.areaSlug || params.areaId || params.slug) &&
    entityData.areaName
  ) {
    entityNames.areaSlug = entityData.areaName;
    entityNames.areaId = entityData.areaName;
    entityNames.slug = entityData.areaName;
  }
  if ((params.modelSlug || params.modelId) && entityData.modelName) {
    entityNames.modelSlug = entityData.modelName;
    entityNames.modelId = entityData.modelName;
  }
  if (params.routeId && entityData.routeName) {
    entityNames.routeId = entityData.routeName; // Routes still use ID
  }

  return entityNames;
};

// Helper function to extract parent IDs from hierarchical paths
export const extractParentIds = (
  currentPath: string
): {
  areaId?: string;
} => {
  const pathSegments = currentPath.split("/").filter(Boolean);
  const parentIds: { areaId?: string } = {};

  // Pattern: /areas/:areaId/... or /areas/:slug/...
  // This might need adjustment if we strictly use slugs now, but keeping ID support logic is safe
  for (let i = 0; i < pathSegments.length; i++) {
    if (pathSegments[i] === "areas" && pathSegments[i + 1]) {
      parentIds.areaId = pathSegments[i + 1];
    }
  }

  return parentIds;
};

// Breadcrumb types and metadata
export interface BreadcrumbItem {
  label: string;
  path: string;
  isActive?: boolean;
}

export interface RouteMetadata {
  breadcrumbLabel: string;
  parentRoute?: string;
  requiresParams?: string[];
}

// Route metadata for breadcrumbs
export const routeMetadata: Record<string, RouteMetadata> = {
  // Landing
  "/": { breadcrumbLabel: "Landing" },
  // Home
  "/home": { breadcrumbLabel: "Home" },

  // Profile
  "/home/profile": { breadcrumbLabel: "Profile", parentRoute: "/home" },
  "/home/profile/ascents": {
    breadcrumbLabel: "My Ascents",
    parentRoute: "/home/profile",
  },

  // Bookmarks
  "/home/bookmarks": { breadcrumbLabel: "Bookmarks", parentRoute: "/home" },

  // Settings
  "/home/settings": { breadcrumbLabel: "Settings", parentRoute: "/home" },
  // Subscription
  "/home/subscription": {
    breadcrumbLabel: "Subscription",
    parentRoute: "/home",
  },

  // Map
  "/home/map": { breadcrumbLabel: "Map", parentRoute: "/home" },

  // Feedback
  "/home/feedback": { breadcrumbLabel: "Feedback", parentRoute: "/home" },

  // Admin
  "/home/admin": { breadcrumbLabel: "Admin", parentRoute: "/home" },

  // Social
  "/home/community": { breadcrumbLabel: "Community", parentRoute: "/home" },
  "/home/feed": { breadcrumbLabel: "Feed", parentRoute: "/home/community" },
  "/home/community/search": {
    breadcrumbLabel: "Search",
    parentRoute: "/home/community",
  },
  "/home/community/friends": {
    breadcrumbLabel: "Friends",
    parentRoute: "/home/profile",
  },
  "/home/profile/:userhandle": {
    breadcrumbLabel: "Profile",
    parentRoute: "/home",
    requiresParams: ["userhandle"],
  },

  // Teams
  "/home/teams": { breadcrumbLabel: "Teams", parentRoute: "/home" },
  "/home/teams/:teamId": {
    breadcrumbLabel: "Team",
    parentRoute: "/home/teams",
    requiresParams: ["teamId"],
  },
  "/home/teams/:teamId/edit": {
    breadcrumbLabel: "Edit Team",
    parentRoute: "/home/teams/:teamId",
    requiresParams: ["teamId"],
  },

  // areas (now top-level entities)
  "/home/admin/content/areas/new": {
    breadcrumbLabel: "New Area",
    parentRoute: "/home/admin",
  },
  "/home/admin/content/areas/:areaSlug/edit": {
    breadcrumbLabel: "Edit Area",
    parentRoute: "/home/areas/:slug", // Link back to view page
    requiresParams: ["areaSlug", "slug"], // Now uses consistent slug params
  },
  "/home/areas/:slug": {
    // Changed from :areaId
    breadcrumbLabel: "Area",
    parentRoute: "/home",
    requiresParams: ["slug"],
  },

  // models (hierarchical under areas)
  "/home/admin/content/models/new": {
    breadcrumbLabel: "New Model",
    parentRoute: "/home/admin",
  },
  "/home/admin/content/models/:modelSlug/edit": {
    breadcrumbLabel: "Edit Model",
    parentRoute: "/home/areas/:slug/models/:modelSlug",
    requiresParams: ["slug", "modelSlug"],
  },
  "/home/areas/:slug/models/:modelSlug": {
    // Changed from :areaId and :modelId
    breadcrumbLabel: "Model",
    parentRoute: "/home/areas/:slug",
    requiresParams: ["slug", "modelSlug"],
  },

  // Routes (hierarchical under models)
  // Routes typically still use IDs as they might not have slugs or are less critical for SEO
  "/home/admin/content/routes/new": {
    breadcrumbLabel: "New Route",
    parentRoute: "/home/admin",
  },
  "/home/admin/content/routes/:routeId/edit": {
    breadcrumbLabel: "Edit Route",
    parentRoute: "/home/areas/:slug/models/:modelSlug", // No specific route view page usually
    requiresParams: ["slug", "modelSlug", "routeId"],
  },
  // If there was a route view page, it would be here.
};

// Helper function to generate breadcrumbs for a given route
export const generateBreadcrumbs = (
  currentPath: string,
  params: Record<string, string> = {},
  entityNames: Record<string, string> = {}
): BreadcrumbItem[] => {
  const breadcrumbs: BreadcrumbItem[] = [];

  // Replace params in path to get the route pattern
  let routePattern = currentPath;
  Object.entries(params).forEach(([key, value]) => {
    routePattern = routePattern.replace(value, `:${key}`);
  });

  const buildBreadcrumbChain = (path: string): void => {
    const metadata = routeMetadata[path];
    if (!metadata) return;

    // Recursively build parent breadcrumbs first
    if (metadata.parentRoute) {
      buildBreadcrumbChain(metadata.parentRoute);
    }

    // Generate the actual path with params
    let actualPath = path;
    if (metadata.requiresParams) {
      metadata.requiresParams.forEach((param) => {
        if (params[param]) {
          actualPath = actualPath.replace(`:${param}`, params[param]);
        }
      });
    }

    // Use entity name if available, otherwise use default label
    let label = metadata.breadcrumbLabel;
    if (metadata.requiresParams && metadata.requiresParams.length >= 1) {
      // For hierarchical routes, prefer the most specific entity name
      const primaryParam =
        metadata.requiresParams[metadata.requiresParams.length - 1];
      const entityName = entityNames[primaryParam];
      if (entityName) {
        label = entityName;
      }
    }

    breadcrumbs.push({
      label,
      path: actualPath,
      isActive: path === routePattern,
    });
  };

  buildBreadcrumbChain(routePattern);
  return breadcrumbs;
};

// Helper function to get breadcrumb metadata for a route
export const getRouteBreadcrumbMetadata = (
  path: string
): RouteMetadata | undefined => {
  return routeMetadata[path];
};

export const paths = {
  index: "/",
  landing: "/",
  pricing: "/pricing",
  home: "/home",
  signin: "/signin",
  emailSignIn: "/signin/email",
  signup: "/signup",
  signUpWithEmail: "/signup/email",
  googleCallback: "/auth/google/callback",
  requestResetPassword: "/request-reset-password",
  resetPassword: "/reset-password",
  onboarding: "/onboarding",
  profile: "/home/profile",
  profileAscents: "/home/profile/ascents",
  bookmarks: "/home/bookmarks",
  settings: "/home/settings",
  subscription: "/home/subscription",
  map: "/home/map",
  feedback: "/home/feedback",
  docs: {
    main: "/docs",
    forClimbers: "/docs/for-climbers",
    forContentCreators: "/docs/for-content-creators",
    addingAreas: "/docs/adding-areas",
    addingModels: "/docs/adding-models",
    view: (viewName: string) => `/docs?view=${viewName}`,
  },
  social: {
    community: "/home/community",
    feed: "/home/feed",
    search: "/home/community/search",
    friends: "/home/community/friends",
    notifications: "/home/notifications",
  },
  publicProfile: (userhandle = ":userhandle") => `/home/profile/${userhandle}`,
  legal: {
    index: "/legal",
    terms: "/legal/terms",
    privacy: "/legal/privacy",
    licenses: "/legal/licenses",
  },
  teams: {
    list: "/home/teams",
    view: (teamId = ":teamId") => `/home/teams/${teamId}`,
    edit: (teamId = ":teamId") => `/home/teams/${teamId}/edit`,
  },
  admin: {
    home: "/home/admin",
    content: {
      areas: {
        new: "/home/admin/content/areas/new",
        // Updated to use slug
        view: (slug = ":slug") => `/home/areas/${slug}`,
        // Edit now uses slug for consistency
        edit: (areaSlug = ":areaSlug") =>
          `/home/admin/content/areas/${areaSlug}/edit`,
      },
      models: {
        new: "/home/admin/content/models/new",
        newInArea: (areaSlug = ":areaSlug") =>
          `/home/admin/content/models/new?areaSlug=${areaSlug}`,
        // Updated to use slugs
        view: (areaSlug = ":slug", modelSlug = ":modelSlug") =>
          `/home/areas/${areaSlug}/models/${modelSlug}`,
        edit: (modelSlug = ":modelSlug") =>
          `/home/admin/content/models/${modelSlug}/edit`,
        // Alias for compatibility
        viewInArea: (areaSlug = ":slug", modelSlug = ":modelSlug") =>
          `/home/areas/${areaSlug}/models/${modelSlug}`,
      },
      routes: {
        new: "/home/admin/content/routes/new",
        edit: (routeId = ":routeId") =>
          `/home/admin/content/routes/${routeId}/edit`,
      },
    },
  },
} as const;

/**
 * Helper function to generate the correct view URL for search results
 * based on content type and available parent IDs
 */
export const generateSearchResultUrl = (
  type: "area" | "model" | "route",
  id: string, // This might be slug now for area/model? Or we need slug passed in.
  // We need to assume that for search results, we might have slug available if the indexer indexed it.
  // If we only have ID, we might have a problem if we removed ID routes entirely.
  // Strategy: The search index should be updated to return slugs.
  // For now, if we assume 'id' passed here IS a slug for area/model, it works.
  // If it's a UUID, it will break.
  // Let's assume the callers will pass slug if available.
  parentId?: string | null,
  parentType?: string | null,
  // For nested content, we might need additional context
  context?: {
    areaId?: string;
    modelId?: string;
    areaSlug?: string;
    modelSlug?: string;
  }
): string => {
  switch (type) {
    case "area":
      // Expecting id to be slug, or we need to handle ID-based redirects if we support them.
      // For now, assuming id is passed as slug effectively or we change param name.
      // To be safe, let's treat 'id' as 'slugOrId'.
      return paths.admin.content.areas.view(context?.areaSlug || id);

    case "model":
      // return paths.admin.content.models.view(context?.areaSlug || ":slug", id);
      return paths.admin.content.models.view(
        context?.areaSlug || ":slug",
        context?.modelSlug || id
      );

    case "route":
      // Routes are on model page
      if (parentType === "model" && parentId && context?.areaSlug) {
        return `${paths.admin.content.models.view(
          context.areaSlug,
          context.modelSlug || parentId // parentId here is model ID/slug
        )}?routeId=${id}&focusRoute=true`;
      }
      return "/";

    default:
      return "/";
  }
};
